import { CircleAlert, KeyRound, Link2Off, LoaderCircle } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import {
  AuthError,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  RESET_LINK_MINUTES,
  authErrorMessage,
  resetPassword,
} from '../lib/authClient';
import { PasswordField } from './PasswordField';

export interface ResetPasswordViewProps {
  /** `?token=…` of the e-mail link. */
  token: string | null;
  /** `?error=…` (Neon Auth redirects expired or used links with `error=INVALID_TOKEN`). */
  linkError?: string | undefined;
  onRequestNewLink: () => void;
  /** The new password is set (the user is not signed in yet). */
  onDone: () => void;
}

export function ResetPasswordView({ token, linkError, onRequestNewLink, onDone }: ResetPasswordViewProps) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<{ text: string; field: 'password' | 'repeat' | 'form' } | null>(null);
  const [state, setState] = useState<'form' | 'busy' | 'invalid-link'>(!token || linkError ? 'invalid-link' : 'form');
  const errorId = useId();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError({ text: `Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`, field: 'password' });
      return;
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
      setError({ text: `Das Passwort darf höchstens ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`, field: 'password' });
      return;
    }
    if (password !== repeat) {
      setError({ text: 'Die beiden Passwörter stimmen nicht überein.', field: 'repeat' });
      return;
    }
    if (!token) return;
    setError(null);
    setState('busy');
    try {
      await resetPassword(token, password);
      onDone();
    } catch (err) {
      if (err instanceof AuthError && err.code === 'invalid_token') {
        setState('invalid-link');
        return;
      }
      setState('form');
      const field = err instanceof AuthError && err.code.startsWith('password_') ? 'password' : 'form';
      setError({ text: authErrorMessage(err, 'reset'), field });
    }
  };

  if (state === 'invalid-link') {
    return (
      <section className="reset glass" aria-labelledby="reset-title">
        <span className="reset__icon reset__icon--warn" aria-hidden="true">
          <Link2Off size={24} />
        </span>
        <h1 id="reset-title" className="reset__title">
          {token || linkError ? 'Link abgelaufen oder schon benutzt' : 'Link unvollständig'}
        </h1>
        <p className="reset__text">
          {token || linkError
            ? `Aus Sicherheitsgründen gilt ein Link zum Festlegen des Passworts nur ${RESET_LINK_MINUTES} Minuten und nur einmal. Fordere einfach einen neuen an.`
            : 'Diesem Link fehlt ein Teil. Öffne ihn direkt aus der E-Mail oder fordere einen neuen an.'}
        </p>
        <div className="form-actions">
          <button type="button" className="btn btn--primary" onClick={onRequestNewLink} data-autofocus>
            Neuen Link anfordern
          </button>
        </div>
      </section>
    );
  }

  const busy = state === 'busy';
  return (
    <section className="reset glass" aria-labelledby="reset-title">
      <span className="reset__icon" aria-hidden="true">
        <KeyRound size={24} />
      </span>
      <h1 id="reset-title" className="reset__title">
        Neues Passwort festlegen
      </h1>
      <p className="reset__text">Danach meldest du dich mit dem neuen Passwort an.</p>
      <form className="form" onSubmit={submit} noValidate>
        <PasswordField
          label="Neues Passwort"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          hint={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`}
          invalid={error?.field === 'password' || error?.field === 'form'}
          describedBy={error ? errorId : undefined}
        />
        <PasswordField
          label="Passwort wiederholen"
          autoComplete="new-password"
          value={repeat}
          onChange={setRepeat}
          invalid={error?.field === 'repeat'}
          describedBy={error?.field === 'repeat' ? errorId : undefined}
        />
        {error && (
          <p id={errorId} className="inline-error" role="alert">
            <CircleAlert size={18} aria-hidden="true" />
            {error.text}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
            {busy ? 'Wird gespeichert …' : 'Passwort speichern'}
          </button>
        </div>
      </form>
    </section>
  );
}
